import type { CSSProperties, RefObject } from "react";
import { useEffect, useState } from "react";
import { useOnClickOutsidePortal } from "../hooks/useOnClickOutsidePortal";

const VIEWPORT_MARGIN = 16;

/** Positions a portaled dropdown panel below (or above, if there's no room) its trigger, and closes it on outside click / Escape. */
export function useFloatingPanel(
  isOpen: boolean,
  onClose: () => void,
  containerRef: RefObject<HTMLElement | null>,
  panelRef: RefObject<HTMLDivElement | null>,
  fallbackPanelWidth = 280,
) {
  const [panelStyle, setPanelStyle] = useState<CSSProperties | null>(null);

  useOnClickOutsidePortal(panelRef, containerRef.current, onClose, isOpen);

  useEffect(() => {
    if (!isOpen) {
      setPanelStyle(null);
      return;
    }

    const updatePosition = () => {
      const trigger = containerRef.current?.getBoundingClientRect();
      if (!trigger) return;

      const panelWidth = panelRef.current?.offsetWidth ?? fallbackPanelWidth;
      const panelHeight = panelRef.current?.offsetHeight ?? 0;
      const alignRight = trigger.left + panelWidth > window.innerWidth - VIEWPORT_MARGIN;
      const wouldOverflowBottom = trigger.bottom + 8 + panelHeight > window.innerHeight - VIEWPORT_MARGIN;
      const hasRoomAbove = trigger.top - 8 - panelHeight >= VIEWPORT_MARGIN;
      const openUp = wouldOverflowBottom && hasRoomAbove;

      setPanelStyle({
        position: "fixed",
        top: openUp ? trigger.top - 8 - panelHeight : trigger.bottom + 8,
        left: alignRight ? trigger.right - panelWidth : trigger.left,
      });
    };

    updatePosition();
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);

    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [isOpen, containerRef, panelRef, fallbackPanelWidth]);

  useEffect(() => {
    if (!isOpen) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isOpen, onClose]);

  return panelStyle;
}
